import * as THREE from 'three'

/**
 * 建筑选中光圈：在目标建筑下方的地面上，画几圈紫色圆环，
 * 一圈一圈向外扩散并淡出（持续循环），用于标示"当前选中了这栋楼"。
 *
 * 用法：
 *   const ripple = createBuildingRipple()
 *   scene.add(ripple.group)
 *   ripple.select({ x, y, z, radius })   // 世界坐标 + 目标半径
 *   ripple.update(dt)                     // 每帧调用
 *   ripple.clear()
 */
export function createBuildingRipple({ color = 0x656993, rings = 3, period = 2.2, segments = 96 } = {}) {
  const group = new THREE.Group()
  group.name = 'buildingRipple'
  group.visible = false
  group.renderOrder = 6

  const ringWidth = 0.34 // 环带宽度（相对当前半径）；开了深度测试后会被建筑挡住，环带粗一点更容易看见
  const items = []
  for (let i = 0; i < rings; i++) {
    const geo = new THREE.RingGeometry(1 - ringWidth, 1, segments)
    geo.rotateX(-Math.PI / 2) // 平铺在 XZ 地面
    const mat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
      // 深度测试必须开着：光圈是地面上的效果，要被建筑体块遮住，
      // 不能像 UI 那样浮在模型上方（之前关掉它是因为相机贴近时看不见，现在靠位置和半径解决）。
      depthTest: true,
      blending: THREE.NormalBlending,
      toneMapped: false,
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.frustumCulled = false
    mesh.renderOrder = 1
    group.add(mesh)
    items.push({ mesh, mat, phase: i / rings })
  }

  let radius = 60
  let elapsed = 0
  let active = false

  return {
    group,
    get active() {
      return active
    },
    /** @param {{x:number,y:number,z:number,radius:number}} target */
    select(target) {
      group.position.set(target.x, target.y, target.z)
      radius = Math.max(12, target.radius || 60)
      elapsed = 0
      active = true
      group.visible = true
    },
    /** 只挪位置、不重启动画（分片微调后让光圈跟着走） */
    moveTo(x, y, z, nextRadius) {
      if (!active) return
      group.position.set(x, y, z)
      if (nextRadius) radius = Math.max(12, nextRadius)
    },
    setRadius(next) {
      radius = Math.max(12, next || radius)
    },
    clear() {
      active = false
      group.visible = false
    },
    /** @param {number} dt 秒 */
    update(dt) {
      if (!active) return
      elapsed += dt
      items.forEach(({ mesh, mat, phase }) => {
        const t = ((elapsed / period + phase) % 1 + 1) % 1
        const s = Math.max(0.001, radius * t)
        mesh.scale.set(s, 1, s)
        // 先亮后淡，末尾完全消失，形成"一圈一圈推开"的观感
        mat.opacity = 0.92 * (1 - t) * (1 - t)
      })
    },
    setColor(next) {
      items.forEach(({ mat }) => mat.color.set(next))
    },
    dispose() {
      items.forEach(({ mesh, mat }) => {
        mesh.geometry.dispose()
        mat.dispose()
      })
      group.clear()
      active = false
    },
  }
}

/**
 * 由包围球半径估一个"地面光环半径"。
 * 因为光圈被建筑遮挡（这是要求的效果），相机贴近时光圈大部分会被挡住，
 * 所以半径要够大——让环能扫到周边的街道/空隙里，才看得见"一圈圈推开"。
 */
export function rippleRadiusFromSphere(sphereRadius, { ratio = 0.75, min = 45, max = 240 } = {}) {
  const r = (sphereRadius || 0) * ratio
  return Math.min(max, Math.max(min, r))
}

/**
 * 按建筑的实际占地推一个"光圈半径"。
 * 光圈开了深度测试、会被楼体挡住：半径若只到楼边，环几乎全程藏在楼里，
 * 看起来就"很小"（几何复杂的楼尤其明显 —— 包围球被楼高/楼群跨度撑大，
 * ×0.75 后反而盖不住实际占地）。所以有顶点实测占地时（拆分网格量出，
 * 见 loadCityTiles 的 MEASURED_FOOTPRINT，经标牌 userData.footprintRadius 透出），
 * 在占地对角半径基础上外扩（×1.25 + 20m），让环能扫出楼体到周边街道才可见；
 * 没有实测值时回退包围球估算，比例比「楼体围栏」的回退激进些（光圈就是要铺出去）。
 * @param {{footprintRadius?:number|null, sphereRadius?:number}} [u] 建筑标牌 userData
 * @returns {number} 光圈半径（米）
 */
export function rippleRadiusForBuilding(u) {
  const fp = u && Number(u.footprintRadius)
  if (Number.isFinite(fp) && fp > 0) {
    return Math.min(320, Math.max(60, fp * 2.5 + 20))
  }
  const sr = u && Number(u.sphereRadius)
  return rippleRadiusFromSphere(Number.isFinite(sr) ? sr : 0, { ratio: 0.9, max: 300 })
}
